<script setup lang="ts">
import { Link } from "@inertiajs/vue3";
import {
  MessageCircleIcon,
  PenLineIcon,
  PlusIcon,
  SearchIcon,
  ShoppingCartIcon,
} from "lucide-vue-next";
import { computed } from "vue";

import { isUnread } from "@/composables/use-thread-read-status";
import { formatRelativeTime } from "@/lib/discussion-thread-helpers";
import type { ThreadSection } from "@/lib/discussion-thread-list";
import type { DiscussionThread } from "@/types";

interface Props {
  organizationName: string;
  sections: ThreadSection[];
  searchValue: string;
  selectedThreadId?: number | null;
  threadHref: (threadId: number) => string;
  isCartThread: (thread: DiscussionThread) => boolean;
  threadBadgeDate: (thread: DiscussionThread) => string;
}

const props = withDefaults(defineProps<Props>(), {
  selectedThreadId: null,
});

const emit = defineEmits<{
  (e: "update:searchValue", value: string): void;
  (e: "create-thread"): void;
  (e: "thread-click", event: MouseEvent, thread: DiscussionThread): void;
}>();

const searchModel = computed({
  get: () => props.searchValue,
  set: (value: string) => emit("update:searchValue", value),
});

const threadItemClass = (thread: DiscussionThread) => {
  const archivedClass = thread.archived ? "opacity-50 saturate-50 hover:opacity-70 hover:saturate-75" : "";

  if (props.selectedThreadId === thread.id) {
    const selectedArchivedClass = thread.archived ? "opacity-65 saturate-75" : "";
    return `w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors bg-primary/[0.08] border-l-2 border-primary ${selectedArchivedClass}`;
  }

  return `w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors hover:bg-foreground/[0.05] ${archivedClass}`;
};

const threadEmojiContainerClass = (thread: DiscussionThread) => {
  if (thread.archived) {
    return "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-foreground/[0.04] text-muted-foreground/70";
  }

  return isUnread(thread)
    ? "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white shadow-sm shadow-emerald-500/30"
    : "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground";
};

const threadEmojiIconClass = (thread: DiscussionThread) => {
  if (thread.archived) return "h-3.5 w-3.5 text-muted-foreground/70";
  return isUnread(thread) ? "h-3.5 w-3.5 text-white" : "h-3.5 w-3.5 text-muted-foreground";
};

const handleThreadClick = (event: MouseEvent, thread: DiscussionThread) => {
  emit("thread-click", event, thread);
};
</script>

<template>
  <div class="px-4 pb-3 pt-4 md:px-5 md:pt-5">
    <div class="mb-4 flex items-center justify-between">
      <div class="flex items-center gap-2.5">
        <div
          class="flex h-7 w-7 items-center justify-center rounded-lg bg-muted"
        >
          <PenLineIcon class="h-3.5 w-3.5 text-muted-foreground" />
        </div>
        <h2 class="text-sm font-semibold tracking-tight text-foreground">
          {{ organizationName }}
        </h2>
      </div>
      <button
        aria-label="Crear nuevo hilo"
        class="flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-foreground/[0.03] hover:text-foreground"
        type="button"
        @click="emit('create-thread')"
      >
        <PlusIcon class="h-4 w-4" />
      </button>
    </div>

    <div class="relative mb-3">
      <SearchIcon
        class="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60"
      />
      <input
        v-model="searchModel"
        class="w-full rounded-lg border border-border bg-muted/30 py-2 pl-9 pr-3 text-sm placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-ring"
        placeholder="Buscar por título o tag..."
        type="text"
      />
    </div>
  </div>

  <div class="flex-1 overflow-y-auto themed-scrollbar px-2 pb-3">
    <section
      v-for="section in sections"
      :key="section.key"
      class="mt-5 first:mt-0"
    >
      <h3 class="px-3 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/70">
        {{ section.label }}
      </h3>
      <p
        v-if="section.groups.length === 0"
        class="px-3 pt-2 text-[12px] text-muted-foreground/70"
      >
        {{ section.emptyMessage }}
      </p>

      <div
        v-for="group in section.groups"
        :key="`${section.key}-${group.label}`"
        class="mt-3 first:mt-2"
      >
        <div class="space-y-1.5">
          <Link
            v-for="thread in group.threads"
            :key="`${section.key}-thread-${thread.id}`"
            :class="threadItemClass(thread)"
            :href="threadHref(thread.id)"
            @click="handleThreadClick($event, thread)"
          >
            <div :class="threadEmojiContainerClass(thread)">
              <ShoppingCartIcon
                v-if="isCartThread(thread)"
                :class="threadEmojiIconClass(thread)"
              />
              <MessageCircleIcon
                v-else
                :class="threadEmojiIconClass(thread)"
              />
            </div>
            <div class="min-w-0 flex-1">
              <div class="flex items-center justify-between gap-2">
                <span class="truncate text-[13px] font-medium text-foreground">
                  {{ thread.title }}
                </span>
                <span class="flex-shrink-0 tabular-nums text-[11px] text-muted-foreground/60">
                  {{ formatRelativeTime(thread.updated_at) }}
                </span>
              </div>
              <div class="mt-px flex items-center justify-between gap-2">
                <span class="truncate text-[11px] text-muted-foreground/60">
                  {{ thread.creator.email }}
                </span>
                <span
                  v-if="threadBadgeDate(thread)"
                  class="flex-shrink-0 text-[10px] text-muted-foreground/60"
                >
                  {{ threadBadgeDate(thread) }}
                </span>
              </div>
            </div>
          </Link>
        </div>
      </div>
    </section>
  </div>
</template>
